import React, { useMemo } from "react";
import { EventProps } from "react-big-calendar";
import { Visit } from "@visitproof/types";
import { Avatar } from "../../../design/avatar";
import { useCase } from "../../../contexts/CaseContext";
import { Divider } from "../../../design/divider";
import { CustodyStatus, Parent } from "@visitproof/types";
import { getInitials } from "../../../utils/string";
import { getStatusLabel } from "../utils/statusColors";

// Extend props to include DOM event handlers
type CustomEventProps = EventProps<object> & {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>;
  onKeyPress?: React.KeyboardEventHandler<HTMLDivElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  onMouseUp?: React.MouseEventHandler<HTMLDivElement>;
  onTouchStart?: React.TouchEventHandler<HTMLDivElement>;
  onTouchEnd?: React.TouchEventHandler<HTMLDivElement>;
};

const EventComponent: React.FC<CustomEventProps> = ({
  event,
  onClick,
  onDoubleClick,
  onKeyPress,
  onMouseDown,
  onMouseUp,
  onTouchStart,
  onTouchEnd,
}) => {
  const { cases } = useCase();
  const visit = (event as any).resource as Visit;
  
  // Memoize case lookup to prevent unnecessary re-renders
  const caseItem = useMemo(() => 
    cases.find((c) => c.id === visit.caseId), 
    [cases, visit.caseId]
  );

  // Memoize non-custodial parents to prevent unnecessary re-renders
  const nonCustodialParents = useMemo(() => {
    if (!caseItem?.parents) return [];
    return (Object.values(caseItem.parents) as Parent[])
      .filter((parent) => parent.custodyStatus === CustodyStatus.NonCustodial);
  }, [caseItem?.parents]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(e as any);
    }
    onKeyPress?.(e);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Visit: ${caseItem?.identifier || 'Unknown case'} - ${getStatusLabel(visit.status)}`}
      className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 px-1 py-0.5 rounded transition-colors hover:bg-opacity-90"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onKeyPress={handleKeyPress}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Case Identifier */}
      {caseItem?.identifier && (
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold text-white">
            {caseItem.identifier}
          </span>
          <span className="text-xs text-white opacity-75">
            ({getStatusLabel(visit.status)})
          </span>
        </div>
      )}

      {/* Supervisor Information */}
      {visit.supervisor && (
        <>
          <Divider className="my-1" />
          <div className="flex items-center gap-1">
            {visit.supervisor.avatarUrl && (
              <Avatar
                src={visit.supervisor.avatarUrl}
                className="size-4"
                initials={getInitials(
                  `${visit.supervisor.firstName} ${visit.supervisor.lastName}`
                )}
              />
            )}
            <span className="text-xs text-white">
              {visit.supervisor.firstName} {visit.supervisor.lastName}
            </span>
          </div>
        </>
      )}

      {/* Non-Custodial Parents */}
      {nonCustodialParents.length > 0 && (
        <>
          <Divider className="my-1" />
          <div className="space-y-1">
            {nonCustodialParents.map((parent) => (
              <div key={parent.id} className="flex items-center gap-1">
                {parent.avatarUrl && (
                  <Avatar
                    src={parent.avatarUrl}
                    className="size-4"
                    initials={getInitials(
                      `${parent.firstName} ${parent.lastName}`
                    )}
                  />
                )}
                <span className="text-xs text-white">
                  {parent.firstName} {parent.lastName}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EventComponent;